import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useServices } from '@/components/ServiceContext';
import log from 'loglevel';

interface ExclusionCacheInitializerProps {
  children: React.ReactNode;
}

/**
 * Component that initializes the exclusion cache before rendering children.
 * This ensures exclusions are always available when components need them.
 */
const ExclusionCacheInitializer: React.FC<ExclusionCacheInitializerProps> = ({
  children,
}) => {
  const { exclusionCacheService } = useServices();
  const [isInitialized, setIsInitialized] = useState(
    () => exclusionCacheService.initialized
  );

  useEffect(() => {
    // Skip initialization if already done
    if (exclusionCacheService.initialized) {
      setIsInitialized(true);
      return;
    }

    const initializeCache = async () => {
      try {
        await exclusionCacheService.initialize();
        setIsInitialized(true);
      } catch (error) {
        log.error('Failed to initialize exclusion cache:', error);
        setIsInitialized(true);
      }
    };

    void initializeCache();
  }, [exclusionCacheService]);

  // Show loading spinner while initializing
  if (!isInitialized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={100} />
      </Box>
    );
  }

  return <>{children}</>;
};

export default ExclusionCacheInitializer;
